/* eslint-disable @typescript-eslint/no-unused-vars */
import React, { FC } from 'react';
import styled, { createGlobalStyle } from 'styled-components';
import GlobalStyles from '@minecraft.global-styles';
import { media } from '@minecraft.themes';
import { BillingPopupProvider, PaywallWrapper, PaywallLocation } from '@blocs.paywall';
import { ThemeProps } from '@consumer-talent.interfaces/styled-theme';
import { TalentPageHeading } from '@consumer-talent.styles/Common';
import IntroduceBenefitsCarousel from '@consumer-talent.components/IntroduceBenefitsCarousel';
import { RouteHelperRoute, useRouteHelper } from '@consumer-talent.utils/routeHelper';
import { Profile } from '@minecraft.graphql-operations';
import { ArtistContextProvider, useArtistContext } from './ArtistContext';
import Meta from './Meta';
import Header from './Header';

const StyledPage = styled.div`
  overflow: hidden;
  background: white;
  min-height: 100vh;
  height: 100%;
  color: ${(props): string => props.theme.colors.primary.black};
  ${media.md`
    overflow: auto;
  `}
`;

const Inner = styled.div`
  margin: 0 1rem;
  padding-bottom: 1.875rem;
  ${media.sm`
    margin: 0 1.875rem;
  `}
`;

const DarkInner = styled.div`
  background-color: ${(props): string => props.theme.colors.accent.ltGrey2};
  min-height: calc(100vh - 4.75rem);
  border-top: 1px solid ${(props): string => props.theme.colors.primary.ltGrey1};
`;

const LightInner = styled.div`
  min-height: calc(100vh - 4.75rem);
  border-top: 1px solid ${(props): string => props.theme.colors.primary.ltGrey1};
`;

const StyledMaxWidthContainer = styled.div<{ $noMaxWidth: boolean }>`
  max-width: ${(props): string => (props.$noMaxWidth ? '100%' : '1440px')};
  margin: 0 auto;
`;

const AppGlobalStyles = createGlobalStyle<ThemeProps>`
  html, body {
    font-family: 'IBM Plex Sans', sans-serif;
    > div:first-child {
      height: 100%;
    }
    ${media.md`
      height: 100%;
      overflow: auto;
    `}
  }
  html {
    box-sizing: border-box;
    font-size: 1rem;
  }
  body {
    padding: 0;
    margin: 0;
    line-height: 2;
  }
  a:not(.active) {
    text-decoration: none;
    color: ${(props): string => props.theme.colors.primary.primary};
  }
  .form-check-input {
    position: relative;
    margin-top: 0;
    margin-left: 0;
  }
  input[type="checkbox"], input[type="radio"] {
    margin: 3px 0.5ex;
  }
  .form-check {
    padding: 0;
  }
`;

const PageTitle = styled(TalentPageHeading)`
  margin: 0;
  padding: 0;
  h2 {
    font-weight: 600;
    max-width: calc(1440px + 3.75rem);
    padding: 1rem;
    margin: auto;
    ${media.sm`
      padding: 1.1rem 1.875rem;
    `}
  }
`;

const StyledPageBg = styled.div<{ blueBg?: boolean; offsetTop: boolean }>`
  background: ${(props): string =>
    props.blueBg ? props.theme.colors.accent.ltBlue : props.theme.colors.primary.white};
  padding-top: ${(props): string => (props.offsetTop ? '1.5rem' : '0')};
`;

export interface PageOwnProps {
  pageTitle?: string;
  blueBg?: boolean;
  withoutNavigation?: boolean;
  noMaxWidthOnContainer?: boolean;
  showLogoutWhenHiddenNav?: boolean;
  innerTheme?: 'dark' | 'light';
  asPaywallLocation?: PaywallLocation;
}

const InnerTheme = ({ innerTheme, children }) => {
  if (innerTheme === 'dark') {
    return <DarkInner data-testid="page-inner-wrapper">{children}</DarkInner>;
  }

  if (innerTheme === 'light') {
    return <LightInner data-testid="page-inner-wrapper">{children}</LightInner>;
  }

  return <Inner data-testid="page-inner-wrapper">{children}</Inner>;
};

const PaywallUI = ({ asPage = false }: { asPage?: boolean }) => {
  const { push } = useRouteHelper();
  const onPaymentCompleteRedirectFn = (redirectRoute: RouteHelperRoute = 'castingBillboard') => {
    push(redirectRoute);
  };
  const isModal = !asPage;
  const onPaymentCompleteRedirect = isModal ? undefined : onPaymentCompleteRedirectFn;
  const { currentProfile } = useArtistContext();

  return (
    <PaywallWrapper
      isModal={!asPage}
      onPaymentCompleteRedirect={onPaymentCompleteRedirect}
      currentProfile={currentProfile as unknown as Profile}
    />
  );
};

/**
 * @deprecated This component is deprecated. Please use PageShell instead.
 */
const Page: FC<PageOwnProps> = ({
  blueBg,
  children,
  innerTheme,
  noMaxWidthOnContainer,
  pageTitle,
  showLogoutWhenHiddenNav,
  withoutNavigation,
  asPaywallLocation,
}) => {
  return (
    <>
      <GlobalStyles />
      <AppGlobalStyles />
      <StyledPage>
        <Meta />
        <IntroduceBenefitsCarousel />
        <ArtistContextProvider>
          <BillingPopupProvider location={asPaywallLocation}>
            <Header
              hideNavigation={withoutNavigation}
              showLogoutWhenHiddenNav={showLogoutWhenHiddenNav}
              location={asPaywallLocation}
            />
            {pageTitle && (
              <PageTitle data-testid="page-title-wrapper">
                <h2>{pageTitle}</h2>
              </PageTitle>
            )}
            <StyledPageBg data-testid="page-styled-bg" blueBg={blueBg} offsetTop={!!pageTitle}>
              <InnerTheme innerTheme={innerTheme}>
                <StyledMaxWidthContainer data-testid="page-max-width-wrapper" $noMaxWidth={noMaxWidthOnContainer}>
                  {children}
                </StyledMaxWidthContainer>
              </InnerTheme>
            </StyledPageBg>
            <PaywallUI asPage={!!asPaywallLocation} />
          </BillingPopupProvider>
        </ArtistContextProvider>
      </StyledPage>
    </>
  );
};

export default Page;
